// RUTA: src/components/fitness/BookTrainingDialog.tsx
// Flujo del cliente para solicitar una sesión personalizada con el coach.
// 3 pasos: elegir día → elegir hora → confirmar y enviar solicitud.

import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ChevronLeft, ChevronRight, Loader2, CalendarCheck, Clock, MessageSquare } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  coachId: string;
  /** Mensaje de notas del coach (cómo llegar, qué llevar, etc.). */
  coachNotes?: string | null;
  /** Cuando una solicitud se envía con éxito, para que el padre refresque. */
  onRequested?: () => void;
}

interface Slot { slot_start: string; slot_end: string; }

const DAYS_OUT = 14; // Cuántos días al futuro se muestran
const WEEKDAY_SHORT = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

export function BookTrainingDialog({ open, onOpenChange, coachId, coachNotes, onRequested }: Props) {
  const [step, setStep] = useState<'day' | 'slot' | 'confirm'>('day');
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [clientNote, setClientNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset al abrir
  useEffect(() => {
    if (!open) return;
    setStep('day');
    setSelectedDay(null);
    setSelectedSlot(null);
    setClientNote('');
    setLoadingSlots(true);

    const today = new Date();
    const fromStr = today.toISOString().slice(0, 10);
    const to = new Date(today);
    to.setDate(today.getDate() + DAYS_OUT);
    const toStr = to.toISOString().slice(0, 10);

    (async () => {
      const { data, error } = await supabase.rpc('get_available_slots', {
        p_coach_id: coachId,
        p_from: fromStr,
        p_to: toStr,
      });
      if (error) toast.error('No se pudieron cargar los horarios');
      setSlots((data as Slot[]) || []);
      setLoadingSlots(false);
    })();
  }, [open, coachId]);

  // Agrupar slots por día (yyyy-mm-dd en hora local del cliente)
  const slotsByDay = useMemo(() => {
    const map = new Map<string, Slot[]>();
    for (const s of slots) {
      const d = new Date(s.slot_start);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(s);
    }
    return map;
  }, [slots]);

  // Cuadrícula de los próximos N días con flag "tiene slots"
  const dayGrid = useMemo(() => {
    const result: { dateKey: string; date: Date; hasSlots: boolean }[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 0; i < DAYS_OUT; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      result.push({ dateKey: key, date: d, hasSlots: (slotsByDay.get(key)?.length ?? 0) > 0 });
    }
    return result;
  }, [slotsByDay]);

  const handleSubmit = async () => {
    if (!selectedSlot) return;
    setSubmitting(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { toast.error('Sesión expirada'); setSubmitting(false); return; }
    const { error } = await supabase.from('training_sessions').insert({
      coach_id: coachId,
      client_id: user.id,
      starts_at: selectedSlot.slot_start,
      ends_at: selectedSlot.slot_end,
      client_note: clientNote.trim() || null,
      status: 'requested',
    });
    setSubmitting(false);
    if (error) {
      // Probablemente conflicto: alguien más reservó el mismo slot
      toast.error(
        error.code === '23505'
          ? 'Ese horario ya fue reservado por alguien más. Elige otro.'
          : 'No se pudo enviar la solicitud',
        { description: error.message?.slice(0, 200) },
      );
      return;
    }
    // Notificar al coach por push (best-effort, no bloquea el flujo)
    const when = new Date(selectedSlot.slot_start).toLocaleString('es-MX', {
      weekday: 'short', day: 'numeric', month: 'short',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
    try {
      await supabase.functions.invoke('send-push', {
        body: {
          user_ids: [coachId],
          title: '🏋️ Nueva solicitud de entrenamiento',
          body: `Un cliente solicitó sesión el ${when}.`,
          url: '/coach',
        },
      });
    } catch (e) {
      console.warn('Push al coach falló:', e);
    }

    toast.success('¡Solicitud enviada! Tu coach te avisará cuando la confirme.');
    onRequested?.();
    onOpenChange(false);
  };

  const formatDate = (d: Date) =>
    `${WEEKDAY_SHORT[d.getDay()]} ${d.getDate()} ${d.toLocaleDateString('es-MX', { month: 'short' })}`;
  const formatTime = (iso: string) =>
    new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false });

  const selectedDaySlots = selectedDay ? slotsByDay.get(selectedDay) ?? [] : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base font-black uppercase tracking-wider">
            <CalendarCheck className="h-4 w-4 text-accent" />
            {step === 'day' ? 'Elige un día' : step === 'slot' ? 'Elige un horario' : 'Confirma tu solicitud'}
          </DialogTitle>
        </DialogHeader>

        {loadingSlots ? (
          <div className="py-12 flex flex-col items-center gap-2 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="text-xs">Cargando horarios disponibles…</span>
          </div>
        ) : (
          <>
            {/* STEP: DAY */}
            {step === 'day' && (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Próximos {DAYS_OUT} días. Los días en verde tienen horarios disponibles.
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {dayGrid.map(d => (
                    <button
                      key={d.dateKey}
                      type="button"
                      disabled={!d.hasSlots}
                      onClick={() => { setSelectedDay(d.dateKey); setStep('slot'); }}
                      className={`p-3 rounded-lg border-2 text-center transition-all
                        ${d.hasSlots
                          ? 'border-accent/40 bg-accent/5 hover:bg-accent/15 hover:border-accent cursor-pointer'
                          : 'border-border bg-muted/20 opacity-50 cursor-not-allowed'}`}
                    >
                      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        {WEEKDAY_SHORT[d.date.getDay()]}
                      </div>
                      <div className="text-lg font-black mt-0.5">{d.date.getDate()}</div>
                      <div className="text-[10px] text-muted-foreground">
                        {d.date.toLocaleDateString('es-MX', { month: 'short' })}
                      </div>
                      {d.hasSlots && (
                        <div className="text-[9px] text-accent mt-1 font-semibold">
                          {slotsByDay.get(d.dateKey)!.length} slot{slotsByDay.get(d.dateKey)!.length !== 1 ? 's' : ''}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
                {dayGrid.every(d => !d.hasSlots) && (
                  <p className="text-xs text-center text-muted-foreground py-4">
                    Tu coach no tiene horarios disponibles en los próximos {DAYS_OUT} días.
                    Contacta directamente para coordinar.
                  </p>
                )}
              </div>
            )}

            {/* STEP: SLOT */}
            {step === 'slot' && selectedDay && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 pb-2 border-b border-border">
                  <Button variant="ghost" size="sm" onClick={() => setStep('day')} className="gap-1 -ml-2">
                    <ChevronLeft className="h-4 w-4" /> Cambiar día
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Tu coach está disponible en estos horarios:
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {selectedDaySlots.map(s => (
                    <button
                      key={s.slot_start}
                      type="button"
                      onClick={() => { setSelectedSlot(s); setStep('confirm'); }}
                      className="p-3 rounded-lg border-2 border-accent/30 bg-accent/5 hover:bg-accent/15 hover:border-accent transition-all text-center"
                    >
                      <Clock className="h-3.5 w-3.5 mx-auto text-accent mb-1" />
                      <div className="text-sm font-black">{formatTime(s.slot_start)}</div>
                      <div className="text-[10px] text-muted-foreground">a {formatTime(s.slot_end)}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* STEP: CONFIRM */}
            {step === 'confirm' && selectedSlot && (
              <div className="space-y-4">
                <div className="bg-accent/10 border border-accent/30 rounded-lg p-3 space-y-1">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    Tu solicitud
                  </div>
                  <div className="text-base font-black">
                    {formatDate(new Date(selectedSlot.slot_start))} · {formatTime(selectedSlot.slot_start)}–{formatTime(selectedSlot.slot_end)}
                  </div>
                </div>

                {coachNotes && coachNotes.trim() && (
                  <div className="text-xs bg-muted/40 rounded-lg p-3 space-y-1">
                    <div className="font-bold text-foreground flex items-center gap-1">
                      <MessageSquare className="h-3 w-3" /> Nota de tu coach
                    </div>
                    <p className="text-muted-foreground whitespace-pre-line">{coachNotes}</p>
                  </div>
                )}

                <div>
                  <Label className="text-xs">Nota opcional (lo que quieras mencionar)</Label>
                  <Textarea
                    placeholder='Ej: "Quiero enfoque en piernas", "Tengo dolor en hombro derecho"'
                    value={clientNote}
                    onChange={e => setClientNote(e.target.value)}
                    className="mt-1 text-sm"
                    rows={3}
                    maxLength={500}
                  />
                </div>

                <p className="text-[10px] text-muted-foreground text-center">
                  Tu coach recibirá la solicitud y te avisará cuando la confirme o sugiera otra hora.
                </p>
              </div>
            )}
          </>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
          {step === 'confirm' && (
            <>
              <Button variant="outline" onClick={() => setStep('slot')} className="gap-1">
                <ChevronLeft className="h-4 w-4" /> Atrás
              </Button>
              <Button onClick={handleSubmit} disabled={submitting} className="gap-2 min-w-[140px]">
                {submitting
                  ? <><Loader2 className="h-4 w-4 animate-spin" /> Enviando…</>
                  : <>Solicitar <ChevronRight className="h-4 w-4" /></>}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
